import axios from 'axios';

const SERVER_URL = process.env.REACT_APP_API_URL || 'http://localhost:1337';

export interface FragmentProgress {
  fragmentId: number;
  fragmentIndex: number;
  completed: boolean;
  started?: boolean;
}

export interface LessonImageFormat {
  name: string;
  hash: string;
  ext: string;
  mime: string;
  path: string | null;
  width: number;
  height: number;
  size: number;
  sizeInBytes?: number;
  url: string;
}

export interface LessonImage {
  id: number;
  documentId?: string;
  name?: string;
  alternativeText?: string | null;
  caption?: string | null;
  width?: number;
  height?: number;
  formats?: {
    thumbnail?: LessonImageFormat;
    small?: LessonImageFormat;
    medium?: LessonImageFormat;
    large?: LessonImageFormat;
  };
  hash?: string;
  ext?: string;
  mime?: string;
  size?: number;
  url: string;
  previewUrl?: string | null;
  provider?: string;
  provider_metadata?: any;
  folderPath?: string;
  createdAt?: string;
  updatedAt?: string;
  publishedAt?: string;
  locale?: string | null;
}

export interface Lesson {
  id: number;
  title?: string;
  content?: string;
  order?: number;
  duration?: number;
  videoUrl?: string;
  isFree?: boolean;
  slug?: string;
  createdAt?: string;
  updatedAt?: string;
  publishedAt?: string;
  locale?: string | null;
  fragmentsProgress?: FragmentProgress[];
  started?: boolean;
  image?: LessonImage | null;
  // ...other lesson fields
}

export interface Chapter {
  id: number;
  documentId?: string;
  title?: string;
  description?: string;
  order?: number;
  duration?: number;
  createdAt?: string;
  updatedAt?: string;
  publishedAt?: string;
  locale?: string | null;
  lessons: Lesson[];
  // ...other chapter fields
}

export interface FeaturedImageFormat {
  url: string;
}

export interface FeaturedImage {
  url: string;
  formats?: {
    thumbnail?: FeaturedImageFormat;
    small?: FeaturedImageFormat;
    medium?: FeaturedImageFormat;
    large?: FeaturedImageFormat;
  };
}

export interface CourseProgressInfo {
  id: number;
  documentId: string;
  completed: boolean;
  progressPercentage: number;
  startedAt?: string;
  completedAt?: string;
  timeSpent: number;
  lastAccessedAt?: string;
  fragmentIndex: number;
  fragmentProgress: Record<string, {
    courseId: number;
    chapterId: number;
    lessonId: number;
    fragmentId: number;
    fragmentIndex: number;
    completed: boolean;
    progressPercentage: number;
    timeSpent: number;
    completedAt: string;
  }>;
  lesson: {
    id: number;
    documentId: string;
    title: string;
    content: string;
    order: number;
    slug: string;
    createdAt: string;
    updatedAt: string;
    publishedAt: string | null;
    locale: string | null;
  };
  chapter: {
    id: number;
    documentId: string;
    title: string;
    description: string;
    order: number;
    createdAt: string;
    updatedAt: string;
    publishedAt: string | null;
    locale: string | null;
  };
  startedLessons: Array<{
    id: number;
    documentId: string;
    title: string;
    content: string;
    order: number;
    slug: string;
    createdAt: string;
    updatedAt: string;
    publishedAt: string | null;
    locale: string | null;
  }>;
  finishedLessons: Array<{
    id: number;
    documentId: string;
    title: string;
    content: string;
    order: number;
    slug: string;
    createdAt: string;
    updatedAt: string;
    publishedAt: string | null;
    locale: string | null;
  }>;
}

export interface ProgressMessage {
  id: number;
  title: string;
  description: string;
  percentProgress: number;
}

export interface Course {
  id: number;
  documentId?: string;
  title: string;
  description: string;
  thumbnail?: string;
  difficultyLevel?: string;
  lessonsCompleted?: number;
  totalLessons?: number;
  isNew?: boolean;
  chapters?: Chapter[];
  featuredImage?: FeaturedImage;
  progressInfo?: CourseProgressInfo;
  progressMessages?: ProgressMessage[];
  slug?: string;
  duration?: number;
  price?: number;
  isFree?: boolean;
  createdAt?: string;
  updatedAt?: string;
  publishedAt?: string;
  locale?: string | null;
}

export interface UpdateCourseProgressRequest {
  courseId: number;
  chapterId: number;
  lessonId: number;
  completed: boolean;
  progressPercentage: number;
  timeSpent: number;
  started?: boolean;
  finished?: boolean;
}

export interface CourseProgress {
  courseId: number;
  progressPercentage: number;
  lessonsCompleted: number;
  totalLessons: number;
  // ...any other fields your backend returns
}

// Types aligned with the Course + Progress JSON shape returned by backend
interface PracticeResults {
  answer?: string;
  isCorrect?: boolean;
  practiceId?: number;
  submittedAt?: string;
  practiceType?: string;
}

interface FragmentProgressDetail {
  courseId: number;
  lessonId: number;
  chapterId: number;
  completed: boolean;
  timeSpent: number;
  fragmentId: number;
  completedAt?: string;
  isCompleted?: boolean;
  fragmentIndex: number;
  lastAccessedAt?: string;
  practiceResults?: PracticeResults | Record<string, never>;
  progressPercentage: number;
}

interface ExtendedCourseProgressInfo {
  id: number;
  documentId: string;
  fragmentIndex: number;
  fragmentProgress: Record<string, FragmentProgressDetail>;
  completed: boolean;
  progressPercentage: number;
  startedAt?: string;
  completedAt?: string;
  timeSpent: number;
  lastAccessedAt?: string;
  createdAt?: string;
  updatedAt?: string;
  publishedAt?: string;
  locale?: string | null;
  lesson: {
    id: number;
    documentId: string;
    title: string;
    content: string;
    order: number;
    slug: string;
    createdAt: string;
    updatedAt: string;
    publishedAt: string | null;
    locale: string | null;
  };
  chapter: {
    id: number;
    documentId: string;
    title: string;
    description: string | null;
    order: number;
    createdAt: string;
    updatedAt: string;
    publishedAt: string | null;
    locale: string | null;
  };
  startedLessons: Array<{
    id: number;
    documentId: string;
    title: string;
    content: string;
    order: number;
    slug: string;
    createdAt: string;
    updatedAt: string;
    publishedAt: string | null;
    locale: string | null;
  }>;
  finishedLessons: Array<{
    id: number;
    documentId: string;
    title: string;
    content: string;
    order: number;
    slug: string;
    createdAt: string;
    updatedAt: string;
    publishedAt: string | null;
    locale: string | null;
  }>;
}

export interface CoursesWithProgress {
  id: number;
  documentId?: string;
  slug?: string;
  createdAt?: string;
  updatedAt?: string;
  // inherited Course props are optional here for flexibility
  title?: string;
  description?: string;
  thumbnail?: string;
  difficultyLevel?: string;
  isNew?: boolean;
  featuredImage?: any;
  duration?: number;
  price?: number;
  isFree?: boolean;
  publishedAt?: string;
  locale?: string | null;
  chapters?: Array<{
    id: number;
    documentId?: string;
    order: number;
    createdAt: string;
    updatedAt: string;
    lessons: Array<{
      id: number;
      documentId: string;
      fragmentsProgress: Array<{ fragmentId: number; fragmentIndex: number; completed: boolean }>;
      order: number;
      slug: string;
      createdAt: string;
      updatedAt: string;
    }>;
  }>;
  progressInfo?: ExtendedCourseProgressInfo;
  totalLessons?: number;
  lessonsCompleted?: number;
}

export interface CourseProgressResponse {
  success: boolean;
  progress: CoursesWithProgress[];
}

export const getCourses = async (): Promise<Course[]> => {
  try {
    const token = localStorage.getItem('jwt_token');
    const response = await axios.get(`${SERVER_URL}/api/courses?populate[0]=chapters&populate[1]=chapters.lessons&populate[2]=featuredImage`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const courses = response.data.data || response.data;
    // Calculate totalLessons for each course and map featuredImage to thumbnail
    return courses.map((course: Course) => {
      let totalLessons = 0;
      if (course.chapters && Array.isArray(course.chapters)) {
        totalLessons = course.chapters.reduce((sum, chapter) => {
          if (chapter.lessons && Array.isArray(chapter.lessons)) {
            return sum + chapter.lessons.length;
          }
          return sum;
        }, 0);
      }
      // Map featuredImage to thumbnail (prefer medium, then small, then url)
      let thumbnail = undefined;
      if (course.featuredImage) {
        const baseUrl = SERVER_URL.replace('/api', '');
        if (course.featuredImage.formats?.medium?.url) {
          thumbnail = baseUrl + course.featuredImage.formats.medium.url;
        } else if (course.featuredImage.formats?.small?.url) {
          thumbnail = baseUrl + course.featuredImage.formats.small.url;
        } else if (course.featuredImage.url) {
          thumbnail = baseUrl + course.featuredImage.url;
        }
      }
      return { ...course, totalLessons, thumbnail };
    });
  } catch (error) {
    throw error;
  }
};

//  "recommended",
//  "trending",
//  "promoted"
export const getFilteredCourse = async (filter: string): Promise<Course[]> => {
  try {
    const token = localStorage.getItem('jwt_token');
    const response = await axios.get(`${SERVER_URL}/api/courses/filtered/${filter}?populate[0]=chapters&populate[1]=chapters.lessons&populate[2]=featuredImage`, {
      headers: token ? {Authorization: `Bearer ${token}`} : {},
    });
    const courses = response.data.data || response.data;
    // Calculate totalLessons for each course and map featuredImage to thumbnail
    return courses.map((course: Course) => {
      let totalLessons = 0;
      if (course.chapters && Array.isArray(course.chapters)) {
        totalLessons = course.chapters.reduce((sum, chapter) => {
          if (chapter.lessons && Array.isArray(chapter.lessons)) {
            return sum + chapter.lessons.length;
          }
          return sum;
        }, 0);
      }
      // Map featuredImage to thumbnail (prefer medium, then small, then url)
      let thumbnail = undefined;
      if (course.featuredImage) {
        const baseUrl = SERVER_URL.replace('/api', '');
        if (course.featuredImage.formats?.medium?.url) {
          thumbnail = baseUrl + course.featuredImage.formats.medium.url;
        } else if (course.featuredImage.formats?.small?.url) {
          thumbnail = baseUrl + course.featuredImage.formats.small.url;
        } else if (course.featuredImage.url) {
          thumbnail = baseUrl + course.featuredImage.url;
        }
      }
      return {...course, totalLessons, thumbnail};
    });

  } catch (error) {
    throw error;
  }
};

export const updateCourseProgress = async (progressData: UpdateCourseProgressRequest): Promise<void> => {
  try {
    const token = localStorage.getItem('jwt_token');
    const response = await axios.post(`${SERVER_URL}/api/product-user/update-course-progress`, progressData, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (response.status !== 200 && response.status !== 201) {
      throw new Error('Failed to update course progress');
    }

    return response.data;
  } catch (error) {
    console.error('Error updating course progress:', error);
    throw error;
  }
};

export const getCourseById = async (courseId: number): Promise<Course> => {
  try {
    const token = localStorage.getItem('jwt_token');
    const response = await axios.get(`${SERVER_URL}/api/courses/${courseId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    const courseData = response.data.data || response.data;

    // Calculate totalLessons for the course
    let totalLessons = 0;
    if (courseData.chapters && Array.isArray(courseData.chapters)) {
      totalLessons = courseData.chapters.reduce((sum: number, chapter: Chapter) => {
        if (chapter.lessons && Array.isArray(chapter.lessons)) {
          return sum + chapter.lessons.length;
        }
        return sum;
      }, 0);
    }

    // Map featuredImage to thumbnail (prefer medium, then small, then url)
    let thumbnail = undefined;
    if (courseData.featuredImage) {
      const baseUrl = SERVER_URL.replace('/api', '');
      if (courseData.featuredImage.formats?.medium?.url) {
        thumbnail = baseUrl + courseData.featuredImage.formats.medium.url;
      } else if (courseData.featuredImage.formats?.small?.url) {
        thumbnail = baseUrl + courseData.featuredImage.formats.small.url;
      } else if (courseData.featuredImage.url) {
        thumbnail = baseUrl + courseData.featuredImage.url;
      }
    }

    return {
      ...courseData,
      totalLessons,
      thumbnail,
      difficultyLevel: (courseData as any).level
    };
  } catch (error) {
    console.error('Error fetching course by ID:', error);
    throw error;
  }
};

// todo filter can be 'started' or 'not-started'
export const getStartedCourses = async (): Promise<Course[]> => {
  try {
    const token = localStorage.getItem('jwt_token');

    if (!token) {
      throw new Error('Authentication token not found');
    }

    const response = await axios.get(`${SERVER_URL}/api/courses/started?filter=started`, {
      headers: { Authorization: `Bearer ${token}` }
    });
// debugger
    const courses = response.data.data || response.data;

    // Calculate totalLessons for each course and map featuredImage to thumbnail
    return courses.map((course: Course) => {
      let totalLessons = 0;
      if (course.chapters && Array.isArray(course.chapters)) {
        totalLessons = course.chapters.reduce((sum, chapter) => {
          if (chapter.lessons && Array.isArray(chapter.lessons)) {
            return sum + chapter.lessons.length;
          }
          return sum;
        }, 0);
      }

      // Map featuredImage to thumbnail (prefer medium, then small, then url)
      let thumbnail = undefined;
      if (course.featuredImage) {
        const baseUrl = SERVER_URL.replace('/api', '');
        if (course.featuredImage.formats?.medium?.url) {
          thumbnail = baseUrl + course.featuredImage.formats.medium.url;
        } else if (course.featuredImage.formats?.small?.url) {
          thumbnail = baseUrl + course.featuredImage.formats.small.url;
        } else if (course.featuredImage.url) {
          thumbnail = baseUrl + course.featuredImage.url;
        }
      }

      return {
        ...course,
        totalLessons,
        thumbnail,
      };
    });
  } catch (error) {
    console.error('Error fetching started courses:', error);
    throw error;
  }
};

export const getCourseWithProgress = async (courseId: number): Promise<Course> => {
  try {
    const token = localStorage.getItem('jwt_token');

    if (!token) {
      throw new Error('Authentication token not found');
    }

    // Get current user to get user ID
    const userResponse = await axios.get(`${SERVER_URL}/api/product-user/me`, {
      headers: { Authorization: `Bearer ${token}` }
    });

    const userId = userResponse.data?.user?.id;
    if (!userId) {
      throw new Error('User ID not found in user data');
    }

    const response = await axios.get(
      `${SERVER_URL}/api/user-course-progress/course/${userId}/${courseId}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    const courseData = response.data.data; // Extract from data wrapper

    // Calculate totalLessons for the course
    let totalLessons = 0;
    if (courseData.chapters && Array.isArray(courseData.chapters)) {
      totalLessons = courseData.chapters.reduce((sum: number, chapter: Chapter) => {
        if (chapter.lessons && Array.isArray(chapter.lessons)) {
          return sum + chapter.lessons.length;
        }
        return sum;
      }, 0);
    }

    // Map featuredImage to thumbnail (prefer medium, then small, then url)
    let thumbnail = undefined;
    if (courseData.featuredImage) {
      const baseUrl = SERVER_URL.replace('/api', '');
      if (courseData.featuredImage.formats?.medium?.url) {
        thumbnail = baseUrl + courseData.featuredImage.formats.medium.url;
      } else if (courseData.featuredImage.formats?.small?.url) {
        thumbnail = baseUrl + courseData.featuredImage.formats.small.url;
      } else if (courseData.featuredImage.url) {
        thumbnail = baseUrl + courseData.featuredImage.url;
      }
    }

    return {
      ...courseData,
      totalLessons,
      thumbnail,
      difficultyLevel: (courseData as any).level
    };
  } catch (error) {
    console.error('Error fetching course with progress:', error);
    throw error;
  }
};
